"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Terminal, Bug, Play, Code, FileWarning } from "lucide-react";

export function CodeReviewPreview() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    const [activeTab, setActiveTab] = useState<"code" | "review">("review");

    return (
        <section ref={ref} className="py-16 md:py-24 px-4 bg-background">
            <div className="container mx-auto max-w-5xl">

                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.5 }}
                    className="relative rounded-xl border border-white/10 bg-[#0A0A0A] overflow-hidden shadow-2xl"
                >
                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-red-500/5 pointer-events-none" />

                    {/* Window Controls / Header */}
                    <div className="flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3 border-b border-white/5 bg-white/5 relative">
                        <div className="flex items-center gap-1.5 md:gap-2">
                            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
                            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
                        </div>
                        <div className="text-[10px] md:text-xs text-muted-foreground font-mono truncate max-w-[180px] md:max-w-none">
                            auth-service.ts — Burg AI Review
                        </div>
                        <div className="w-6 md:w-10"></div>
                    </div>

                    {/* Mobile Tab Switcher */}
                    <div className="flex lg:hidden border-b border-white/5 bg-[#0A0A0A]">
                        <button
                            onClick={() => setActiveTab("code")}
                            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === "code"
                                ? "text-white bg-white/5 border-b-2 border-primary"
                                : "text-muted-foreground hover:text-white"
                                }`}
                        >
                            <Code className="w-3.5 h-3.5" />
                            Code
                        </button>
                        <button
                            onClick={() => setActiveTab("review")}
                            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === "review"
                                ? "text-white bg-white/5 border-b-2 border-red-500"
                                : "text-muted-foreground hover:text-white"
                                }`}
                        >
                            <FileWarning className="w-3.5 h-3.5" />
                            Report
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-red-500/20 text-red-400">3</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[300px] md:min-h-[420px]">
                        {/* Left: Code */}
                        <div className={`p-4 md:p-6 font-mono text-xs md:text-sm border-r border-white/5 bg-[#0A0A0A] text-gray-300 overflow-x-auto ${activeTab === "code" ? "block" : "hidden lg:block"}`}>
                            <div className="flex gap-3 md:gap-4 min-w-[400px]">
                                <div className="text-[10px] md:text-xs text-muted-foreground/50 select-none flex flex-col text-right pr-3 md:pr-4 border-r border-white/5">
                                    {Array.from({ length: 12 }).map((_, i) => (
                                        <span key={i} className="leading-5 md:leading-6">{i + 1}</span>
                                    ))}
                                </div>
                                <div className="flex-1">
                                    <p className="leading-5 md:leading-6"><span className="text-purple-400">async</span> <span className="text-blue-400">function</span> <span className="text-yellow-300">updateUserRole</span>(userId, newRole, requestorId) {"{"}</p>
                                    <p className="leading-5 md:leading-6 pl-4"><span className="text-gray-500">{/* Check if requestor has admin permissions */}</span></p>
                                    <p className="leading-5 md:leading-6 pl-4"><span className="text-purple-400">const</span> requestor = <span className="text-purple-400">await</span> getUserById(requestorId);</p>
                                    <p className="leading-5 md:leading-6 pl-4 bg-red-500/10 -mx-2 px-2 rounded"><span className="text-purple-400">if</span> (requestor.role !== <span className="text-green-400">&apos;admin&apos;</span>) <span className="text-purple-400">throw</span> <span className="text-blue-400">new</span> Error(<span className="text-green-400">&apos;Unauthorized&apos;</span>);</p>
                                    <p className="leading-5 md:leading-6 pl-4">&nbsp;</p>
                                    <p className="leading-5 md:leading-6 pl-4"><span className="text-gray-500">{/* Update role and invalidate cache */}</span></p>
                                    <p className="leading-5 md:leading-6 pl-4 bg-yellow-500/10 -mx-2 px-2 rounded"><span className="text-purple-400">await</span> db.<span className="text-blue-400">update</span>(<span className="text-green-400">&apos;users&apos;</span>, &lbrace; role: newRole &rbrace;, &lbrace; id: userId &rbrace;);</p>
                                    <p className="leading-5 md:leading-6 pl-4 bg-yellow-500/10 -mx-2 px-2 rounded">cache.<span className="text-blue-400">invalidate</span>(<span className="text-green-400">`user:$&lbrace;requestorId&rbrace;`</span>);</p>
                                    <p className="leading-5 md:leading-6 pl-4">&nbsp;</p>
                                    <p className="leading-5 md:leading-6 pl-4"><span className="text-gray-500">{/* Breaking change: old API returned boolean */}</span></p>
                                    <p className="leading-5 md:leading-6 pl-4 bg-orange-500/10 -mx-2 px-2 rounded"><span className="text-purple-400">return</span> &lbrace; success: <span className="text-blue-400">true</span>, oldRole: <span className="text-purple-400">await</span> getOldRole(userId) &rbrace;;</p>
                                    <p className="leading-5 md:leading-6">{"}"}</p>
                                </div>
                            </div>
                        </div>

                        {/* Right: AI Output */}
                        <div className={`bg-[#111111] p-4 md:p-6 text-sm ${activeTab === "review" ? "block" : "hidden lg:block"}`}>
                            <div className="flex items-center gap-2 mb-4 md:mb-6">
                                <div className="h-5 w-5 md:h-6 md:w-6 rounded bg-primary text-primary-foreground flex items-center justify-center">
                                    <Terminal className="w-3 h-3 md:w-4 md:h-4" />
                                </div>
                                <span className="font-semibold text-white text-sm md:text-base">Burg AI Report</span>
                                <span className="ml-auto text-[10px] md:text-xs px-2 py-0.5 md:py-1 rounded bg-red-500/10 text-red-400 border border-red-500/20">Issues Found</span>
                            </div>

                            <div className="space-y-3 md:space-y-4">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                    className="p-3 md:p-4 rounded-lg bg-[#0A0A0A] border border-white/5 border-l-2 border-l-red-500"
                                >
                                    <h4 className="flex items-center gap-2 font-semibold text-red-400 mb-1.5 md:mb-2 text-xs md:text-sm">
                                        <Bug className="w-3.5 h-3.5 md:w-4 md:h-4" /> Incorrect Cache Invalidation
                                    </h4>
                                    <p className="text-muted-foreground text-[11px] md:text-xs leading-relaxed mb-2 md:mb-3">
                                        Line 8. Cache invalidation only clears the <code className="text-red-300 bg-red-500/10 px-1 rounded">requestor</code>&apos;s cache, not the <code className="text-red-300 bg-red-500/10 px-1 rounded">target user</code>&apos;s. This creates stale permission data.
                                    </p>
                                    <div className="bg-black/50 p-2 rounded text-[10px] md:text-xs font-mono text-green-400 overflow-x-auto">
                                        cache.invalidate(`user:$&lbrace;userId&rbrace;`);
                                    </div>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8 }}
                                    className="p-3 md:p-4 rounded-lg bg-[#0A0A0A] border border-white/5 border-l-2 border-l-orange-500"
                                >
                                    <h4 className="flex items-center gap-2 font-semibold text-orange-400 mb-1.5 md:mb-2 text-xs md:text-sm">
                                        <Play className="w-3.5 h-3.5 md:w-4 md:h-4" /> Auth Bypass via Role Mismatch
                                    </h4>
                                    <p className="text-muted-foreground text-[11px] md:text-xs leading-relaxed mb-2 md:mb-3">
                                        Line 5. Role check uses cached data, but cache invalidation happens after DB update. Race condition allows privilege escalation during concurrent requests.
                                    </p>
                                    <div className="bg-black/50 p-2 rounded text-[10px] md:text-xs font-mono text-green-400 overflow-x-auto">
                                        Check fresh DB data: const freshRequestor = await getUserById(requestorId);
                                    </div>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.1 }}
                                    className="p-3 md:p-4 rounded-lg bg-[#0A0A0A] border border-white/5 border-l-2 border-l-yellow-500"
                                >
                                    <h4 className="flex items-center gap-2 font-semibold text-yellow-400 mb-1.5 md:mb-2 text-xs md:text-sm">
                                        <Terminal className="w-3.5 h-3.5 md:w-4 md:h-4" /> Breaking Backward Compatibility
                                    </h4>
                                    <p className="text-muted-foreground text-[11px] md:text-xs leading-relaxed">
                                        Line 12. API now returns object instead of boolean. This breaks existing clients. Add version parameter or maintain old return format with deprecation warning.
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
